import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import './index.scss';

// 定义弹出位置类型
type PopoverPlacement =
  | 'top'
  | 'top-start'
  | 'top-end'
  | 'bottom'
  | 'bottom-start'
  | 'bottom-end'
  | 'left'
  | 'left-start'
  | 'left-end'
  | 'right'
  | 'right-start'
  | 'right-end';

// 定义触发方式类型
type PopoverTrigger = 'click' | 'hover';

// 定义弹出内容项类型
interface PopoverAction {
  key: string;
  text: React.ReactNode;
  icon?: React.ReactNode;
  disabled?: boolean;
  onClick?: () => void;
}

// 定义组件属性类型
interface PopoverProps {
  children: React.ReactElement;
  content?: React.ReactNode;
  actions?: PopoverAction[];
  placement?: PopoverPlacement;
  trigger?: PopoverTrigger;
  visible?: boolean;
  onVisibleChange?: (visible: boolean) => void;
  getContainer?: () => HTMLElement;
  destroyOnHide?: boolean;
  stopPropagation?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

// 定义位置计算结果类型
interface Position {
  top: number;
  left: number;
  transformOrigin: string;
  arrowStyle?: React.CSSProperties;
}

const Popover = function (props: PopoverProps) {
  const children = props.children;
  const content = props.content;
  const actions = props.actions || [];
  const placement = props.placement || 'bottom';
  const trigger = props.trigger || 'click';
  const controlledVisible = props.visible;
  const onVisibleChange = props.onVisibleChange;
  const getContainer = props.getContainer;
  const destroyOnHide = props.destroyOnHide || false;
  const stopPropagation = props.stopPropagation !== false; // 默认true
  const className = props.className || '';
  const style = props.style || {};

  const [internalVisible, setInternalVisible] = useState(false);
  const [position, setPosition] = useState<Position>({
    top: 0,
    left: 0,
    transformOrigin: 'center top'
  });
  const triggerRef = useRef<HTMLElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const hoverTimerRef = useRef<any>(null);

  // 判断是否为受控组件
  const isControlled = controlledVisible !== undefined;
  const visible = isControlled ? controlledVisible : internalVisible;

  // 显示/隐藏处理函数
  const handleVisibleChange = function (newVisible: boolean) {
    if (!isControlled) {
      setInternalVisible(newVisible);
    }
    if (onVisibleChange) {
      onVisibleChange(newVisible);
    }
  };

  // 计算弹出框位置 - 参考 Floating-UI 算法
  const calculatePosition = function (): Position {
    if (!triggerRef.current || !popoverRef.current) {
      return { top: 0, left: 0, transformOrigin: 'center top' };
    }

    // 获取触发元素的位置信息
    const referenceRect = triggerRef.current.getBoundingClientRect();
    const floatingRect = popoverRef.current.getBoundingClientRect();

    // 获取滚动偏移 - Android 7 兼容处理
    const scrollTop =
      window.pageYOffset ||
      document.documentElement.scrollTop ||
      document.body.scrollTop ||
      0;
    const scrollLeft =
      window.pageXOffset ||
      document.documentElement.scrollLeft ||
      document.body.scrollLeft ||
      0;

    // 获取视窗尺寸 - Android 7 兼容处理
    const viewportWidth =
      window.innerWidth ||
      document.documentElement.clientWidth ||
      document.body.clientWidth ||
      320;
    const viewportHeight =
      window.innerHeight ||
      document.documentElement.clientHeight ||
      document.body.clientHeight ||
      568;

    // 计算基础坐标 - 参考 Floating-UI 的 computeCoordsFromPlacement
    const coords = computeCoordsFromPlacement(
      referenceRect,
      floatingRect,
      placement
    );

    // 应用 offset middleware
    const offsetCoords = applyOffset(coords, 8);

    // 应用 flip middleware - 防止溢出
    const flippedCoords = applyFlip(
      offsetCoords,
      referenceRect,
      floatingRect,
      viewportWidth,
      viewportHeight,
      placement
    );

    // 应用 shift middleware - 保持在视窗内
    const shiftedCoords = applyShift(
      flippedCoords,
      floatingRect,
      viewportWidth,
      viewportHeight,
      8 // padding
    );

    // 转换为绝对定位坐标
    const finalTop = shiftedCoords.y + scrollTop;
    const finalLeft = shiftedCoords.x + scrollLeft;

    // 计算 transform origin
    const transformOrigin = getTransformOrigin(placement);

    // 计算箭头位置
    const arrowStyle = calculateArrowPosition(
      referenceRect,
      floatingRect,
      { x: shiftedCoords.x, y: shiftedCoords.y },
      placement
    );

    return {
      top: finalTop,
      left: finalLeft,
      transformOrigin,
      arrowStyle
    };
  };

  // 参考 Floating-UI 的 computeCoordsFromPlacement 函数
  const computeCoordsFromPlacement = function (
    referenceRect: DOMRect,
    floatingRect: DOMRect,
    placement: PopoverPlacement
  ) {
    const referenceX = referenceRect.x;
    const referenceY = referenceRect.y;
    const referenceWidth = referenceRect.width;
    const referenceHeight = referenceRect.height;

    const floatingWidth = floatingRect.width || 200; // 默认宽度
    const floatingHeight = floatingRect.height || 100; // 默认高度

    let x = 0;
    let y = 0;

    // 根据主要方向计算基础位置
    const mainAxis = placement.split('-')[0];

    switch (mainAxis) {
      case 'top':
        x = referenceX + referenceWidth / 2 - floatingWidth / 2;
        y = referenceY - floatingHeight;
        break;
      case 'bottom':
        x = referenceX + referenceWidth / 2 - floatingWidth / 2;
        y = referenceY + referenceHeight;
        break;
      case 'left':
        x = referenceX - floatingWidth;
        y = referenceY + referenceHeight / 2 - floatingHeight / 2;
        break;
      case 'right':
        x = referenceX + referenceWidth;
        y = referenceY + referenceHeight / 2 - floatingHeight / 2;
        break;
    }

    // 根据对齐方式调整位置
    if (placement.indexOf('-start') !== -1) {
      if (mainAxis === 'top' || mainAxis === 'bottom') {
        x = referenceX;
      } else {
        y = referenceY;
      }
    } else if (placement.indexOf('-end') !== -1) {
      if (mainAxis === 'top' || mainAxis === 'bottom') {
        x = referenceX + referenceWidth - floatingWidth;
      } else {
        y = referenceY + referenceHeight - floatingHeight;
      }
    }

    return { x, y };
  };

  // 应用偏移 middleware
  const applyOffset = function (
    coords: { x: number; y: number },
    offset: number
  ) {
    const mainAxis = placement.split('-')[0];

    switch (mainAxis) {
      case 'top':
        return { x: coords.x, y: coords.y - offset };
      case 'bottom':
        return { x: coords.x, y: coords.y + offset };
      case 'left':
        return { x: coords.x - offset, y: coords.y };
      case 'right':
        return { x: coords.x + offset, y: coords.y };
      default:
        return coords;
    }
  };

  // 应用翻转 middleware - 防止溢出
  const applyFlip = function (
    coords: { x: number; y: number },
    referenceRect: DOMRect,
    floatingRect: DOMRect,
    viewportWidth: number,
    viewportHeight: number,
    currentPlacement: PopoverPlacement
  ) {
    const floatingWidth = floatingRect.width || 200;
    const floatingHeight = floatingRect.height || 100;

    // 检查是否溢出
    const overflow = {
      top: coords.y < 0,
      bottom: coords.y + floatingHeight > viewportHeight,
      left: coords.x < 0,
      right: coords.x + floatingWidth > viewportWidth
    };

    // 简单的翻转逻辑
    const mainAxis = currentPlacement.split('-')[0];
    let newCoords = { x: coords.x, y: coords.y };

    if (mainAxis === 'top' && overflow.top) {
      // 翻转到底部
      newCoords.y = referenceRect.y + referenceRect.height + 8;
    } else if (mainAxis === 'bottom' && overflow.bottom) {
      // 翻转到顶部
      newCoords.y = referenceRect.y - floatingHeight - 8;
    } else if (mainAxis === 'left' && overflow.left) {
      // 翻转到右侧
      newCoords.x = referenceRect.x + referenceRect.width + 8;
    } else if (mainAxis === 'right' && overflow.right) {
      // 翻转到左侧
      newCoords.x = referenceRect.x - floatingWidth - 8;
    }

    return newCoords;
  };

  // 应用位移 middleware - 保持在视窗内
  const applyShift = function (
    coords: { x: number; y: number },
    floatingRect: DOMRect,
    viewportWidth: number,
    viewportHeight: number,
    padding: number
  ) {
    const floatingWidth = floatingRect.width || 200;
    const floatingHeight = floatingRect.height || 100;

    let newCoords = { x: coords.x, y: coords.y };

    // 水平位移
    if (newCoords.x < padding) {
      newCoords.x = padding;
    } else if (newCoords.x + floatingWidth > viewportWidth - padding) {
      newCoords.x = viewportWidth - floatingWidth - padding;
    }

    // 垂直位移
    if (newCoords.y < padding) {
      newCoords.y = padding;
    } else if (newCoords.y + floatingHeight > viewportHeight - padding) {
      newCoords.y = viewportHeight - floatingHeight - padding;
    }

    return newCoords;
  };

  // 获取 transform origin
  const getTransformOrigin = function (placement: PopoverPlacement): string {
    const mainAxis = placement.split('-')[0];
    const alignment = placement.split('-')[1];

    let origin = '';

    switch (mainAxis) {
      case 'top':
        origin = 'bottom';
        break;
      case 'bottom':
        origin = 'top';
        break;
      case 'left':
        origin = 'right';
        break;
      case 'right':
        origin = 'left';
        break;
    }

    if (alignment === 'start') {
      origin =
        mainAxis === 'top' || mainAxis === 'bottom'
          ? 'left ' + origin
          : origin + ' top';
    } else if (alignment === 'end') {
      origin =
        mainAxis === 'top' || mainAxis === 'bottom'
          ? 'right ' + origin
          : origin + ' bottom';
    } else {
      origin =
        mainAxis === 'top' || mainAxis === 'bottom'
          ? 'center ' + origin
          : origin + ' center';
    }

    return origin;
  };

  // 计算箭头位置
  const calculateArrowPosition = function (
    referenceRect: DOMRect,
    floatingRect: DOMRect,
    floatingCoords: { x: number; y: number },
    placement: PopoverPlacement
  ): React.CSSProperties {
    const mainAxis = placement.split('-')[0];
    const alignment = placement.split('-')[1];

    // 计算触发元素中心相对于弹出框的位置
    const referenceCenterX = referenceRect.x + referenceRect.width / 2;
    const referenceCenterY = referenceRect.y + referenceRect.height / 2;

    const floatingLeft = floatingCoords.x;
    const floatingTop = floatingCoords.y;

    let arrowStyle: React.CSSProperties = {};

    if (mainAxis === 'top' || mainAxis === 'bottom') {
      // 水平方向的箭头定位
      const arrowLeft = referenceCenterX - floatingLeft;

      if (alignment === 'start') {
        // 左对齐时，箭头应该在触发元素中心
        arrowStyle.left = Math.max(
          12,
          Math.min(arrowLeft, (floatingRect.width || 200) - 12)
        );
      } else if (alignment === 'end') {
        // 右对齐时，箭头应该在触发元素中心
        arrowStyle.left = Math.max(
          12,
          Math.min(arrowLeft, (floatingRect.width || 200) - 12)
        );
      } else {
        // 居中对齐
        arrowStyle.left = '50%';
        arrowStyle.marginLeft = '-6px';
      }
    } else if (mainAxis === 'left' || mainAxis === 'right') {
      // 垂直方向的箭头定位
      const arrowTop = referenceCenterY - floatingTop;

      if (alignment === 'start') {
        arrowStyle.top = Math.max(
          12,
          Math.min(arrowTop, (floatingRect.height || 100) - 12)
        );
      } else if (alignment === 'end') {
        arrowStyle.top = Math.max(
          12,
          Math.min(arrowTop, (floatingRect.height || 100) - 12)
        );
      } else {
        arrowStyle.top = '50%';
        arrowStyle.marginTop = '-6px';
      }
    }

    return arrowStyle;
  };

  // 更新位置
  const updatePosition = function () {
    if (visible) {
      const newPosition = calculatePosition();
      setPosition(newPosition);
    }
  };

  // 点击事件处理
  const handleClick = function (e: React.MouseEvent) {
    if (stopPropagation) {
      e.stopPropagation();
    }
    if (trigger === 'click') {
      handleVisibleChange(!visible);
    }
  };

  // 鼠标进入事件处理
  const handleMouseEnter = function () {
    if (trigger === 'hover') {
      if (hoverTimerRef.current) {
        clearTimeout(hoverTimerRef.current);
        hoverTimerRef.current = null;
      }
      handleVisibleChange(true);
    }
  };

  // 鼠标离开事件处理
  const handleMouseLeave = function () {
    if (trigger === 'hover') {
      hoverTimerRef.current = setTimeout(function () {
        handleVisibleChange(false);
      }, 100);
    }
  };

  // 弹出框鼠标进入事件处理
  const handlePopoverMouseEnter = function () {
    if (trigger === 'hover' && hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
  };

  // 弹出框鼠标离开事件处理
  const handlePopoverMouseLeave = function () {
    if (trigger === 'hover') {
      hoverTimerRef.current = setTimeout(function () {
        handleVisibleChange(false);
      }, 100);
    }
  };

  // 点击外部关闭
  const handleDocumentClick = function (e: Event) {
    if (trigger === 'click' && visible) {
      const target = e.target as HTMLElement;
      if (
        triggerRef.current &&
        !triggerRef.current.contains(target) &&
        popoverRef.current &&
        !popoverRef.current.contains(target)
      ) {
        handleVisibleChange(false);
      }
    }
  };

  // 动作项点击处理
  const handleActionClick = function (action: PopoverAction) {
    if (!action.disabled && action.onClick) {
      action.onClick();
    }
    handleVisibleChange(false);
  };

  // 监听文档点击事件
  useEffect(
    function () {
      if (visible && trigger === 'click') {
        document.addEventListener('click', handleDocumentClick);
        return function () {
          document.removeEventListener('click', handleDocumentClick);
        };
      }
    },
    [visible, trigger]
  );

  // 监听窗口大小变化和滚动事件
  useEffect(
    function () {
      if (visible) {
        updatePosition();

        const handleResize = function () {
          updatePosition();
        };

        const handleScroll = function () {
          updatePosition();
        };

        window.addEventListener('resize', handleResize);
        window.addEventListener('scroll', handleScroll, true);

        return function () {
          window.removeEventListener('resize', handleResize);
          window.removeEventListener('scroll', handleScroll, true);
        };
      }
    },
    [visible, placement]
  );

  // 清理定时器
  useEffect(function () {
    return function () {
      if (hoverTimerRef.current) {
        clearTimeout(hoverTimerRef.current);
      }
    };
  }, []);

  // 渲染弹出内容
  const renderContent = function () {
    if (content) {
      return content;
    }

    if (actions.length > 0) {
      return (
        <div className="popover-actions">
          {actions.map(function (action) {
            return (
              <div
                key={action.key}
                className={
                  'popover-action-item' + (action.disabled ? ' disabled' : '')
                }
                onClick={function () {
                  handleActionClick(action);
                }}
              >
                {action.icon && (
                  <span className="popover-action-icon">{action.icon}</span>
                )}
                <span className="popover-action-text">{action.text}</span>
              </div>
            );
          })}
        </div>
      );
    }

    return null;
  };

  // 渲染弹出框
  const renderPopover = function () {
    if (!visible && destroyOnHide) {
      return null;
    }

    const popoverContent = (
      <div
        ref={popoverRef}
        className={
          'popover-wrapper ' +
          (visible ? 'visible' : 'hidden') +
          ' ' +
          className
        }
        style={(function () {
          // 手动合并样式对象，兼容Chrome52
          const baseStyle: React.CSSProperties = {
            position: 'absolute' as 'absolute',
            top: position.top + 'px',
            left: position.left + 'px',
            transformOrigin: position.transformOrigin,
            zIndex: 1000
          };
          // 手动复制自定义样式
          const keys = Object.keys(style || {});
          for (let i = 0; i < keys.length; i++) {
            const key = keys[i];
            (baseStyle as any)[key] = (style as any)[key];
          }
          return baseStyle;
        })()}
        onMouseEnter={handlePopoverMouseEnter}
        onMouseLeave={handlePopoverMouseLeave}
      >
        <div className={'popover-content placement-' + placement}>
          <div className="popover-arrow" style={position.arrowStyle}></div>
          <div className="popover-inner">{renderContent()}</div>
        </div>
      </div>
    );

    // 获取容器
    const container = getContainer ? getContainer() : document.body;

    // 使用 ReactDOM.createPortal 在指定容器中渲染
    return ReactDOM.createPortal(popoverContent, container);
  };

  // 克隆子元素并添加事件处理
  const clonedChildren = React.cloneElement(children, {
    ref: triggerRef,
    onClick: function (e: React.MouseEvent) {
      // 先执行原有的onClick
      if (children.props.onClick) {
        children.props.onClick(e);
      }
      // 再执行popover的点击处理
      handleClick(e);
    },
    onMouseEnter: function (e: React.MouseEvent) {
      // 先执行原有的onMouseEnter
      if (children.props.onMouseEnter) {
        children.props.onMouseEnter(e);
      }
      // 再执行popover的鼠标进入处理
      handleMouseEnter();
    },
    onMouseLeave: function (e: React.MouseEvent) {
      // 先执行原有的onMouseLeave
      if (children.props.onMouseLeave) {
        children.props.onMouseLeave(e);
      }
      // 再执行popover的鼠标离开处理
      handleMouseLeave();
    }
  });

  return (
    <>
      {clonedChildren}
      {renderPopover()}
    </>
  );
};

export default Popover;
